import { findRouteConfig, BreadcrumbConfig } from '../config/inspectorRoutes'

export interface Breadcrumb {
  label: string
  path: string
  isLink: boolean
}

interface PathParams {
  [key: string]: string
}

// Extract parameters from pathname based on pattern
const extractParams = (pattern: string, pathname: string): PathParams => {
  const params: PathParams = {}
  
  // Split both pattern and pathname into segments
  const patternSegments = pattern.split('/')
  const pathSegments = pathname.split('/')
  
  // Extract parameter values
  patternSegments.forEach((segment, index) => {
    if (segment.startsWith(':')) {
      const paramName = segment.slice(1)
      params[paramName] = pathSegments[index] || ''
    }
  })
  
  return params
}

// Generate breadcrumbs from pathname.
// `overrideLastCrumb` lets a page push a resolved label up (e.g. an exception's
// `name_short` fetched from the API) to replace the sync slug-based resolver
// output for the terminal dynamic crumb.
export const generateBreadcrumbs = (
  pathname: string,
  overrideLastCrumb?: string,
): Breadcrumb[] => {
  const routeConfig = findRouteConfig(pathname)

  if (!routeConfig) {
    // Fallback to basic path-based breadcrumbs if no config found
    return generateFallbackBreadcrumbs(pathname)
  }

  const params = extractParams(routeConfig.path, pathname)
  const breadcrumbs: Breadcrumb[] = []

  // Build breadcrumb trail from route config
  routeConfig.breadcrumbs.forEach((config, index) => {
    const isLastItem = index === routeConfig.breadcrumbs.length - 1

    // Handle dynamic labels
    let label = config.label
    if (config.dynamic && params[config.dynamic.param]) {
      label = config.dynamic.resolver(params[config.dynamic.param])
    }

    // Override the terminal dynamic crumb when the caller supplied one.
    if (isLastItem && config.dynamic && overrideLastCrumb) {
      label = overrideLastCrumb
    }

    // Skip empty labels (can happen with dynamic segments)
    if (!label) return

    // Build path up to this breadcrumb
    const path = buildPathForBreadcrumb(pathname, index, routeConfig.breadcrumbs.length)

    breadcrumbs.push({
      label,
      path,
      isLink: !isLastItem
    })
  })

  return breadcrumbs
}

// Build the path for a breadcrumb at a specific index
const buildPathForBreadcrumb = (fullPath: string, index: number, totalCrumbs: number): string => {
  // Extract fp_code from the path
  const fpCodeMatch = fullPath.match(/\/inspect\/([^/]+)/)
  const fpCode = fpCodeMatch ? fpCodeMatch[1] : ''
  
  if (!fpCode) return fullPath
  
  // For dashboard
  if (index === 0) {
    return `/inspect/${fpCode}`
  }
  
  // For other breadcrumbs, build path based on the URL segments
  const segments = fullPath.replace(`/inspect/${fpCode}`, '').split('/').filter(Boolean)
  
  // Build path up to the current breadcrumb index
  const pathSegments = segments.slice(0, Math.min(index, segments.length))
  
  if (pathSegments.length === 0) {
    return `/inspect/${fpCode}`
  }
  
  return `/inspect/${fpCode}/${pathSegments.join('/')}`
}

// Fallback breadcrumb generation when no route config is found
const generateFallbackBreadcrumbs = (pathname: string): Breadcrumb[] => {
  const fpCodeMatch = pathname.match(/\/inspect\/([^/]+)/)
  const fpCode = fpCodeMatch ? fpCodeMatch[1] : ''
  
  if (!fpCode) return []
  
  const segments = pathname
    .replace(`/inspect/${fpCode}`, '')
    .split('/')
    .filter(Boolean)
  
  const breadcrumbs: Breadcrumb[] = [
    {
      label: 'Dashboard',
      path: `/inspect/${fpCode}`,
      isLink: segments.length > 0
    }
  ]
  
  let currentPath = `/inspect/${fpCode}`
  
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`
    const isLastSegment = index === segments.length - 1
    
    // Convert segment to title case
    const label = segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    
    breadcrumbs.push({
      label,
      path: currentPath,
      isLink: !isLastSegment
    })
  })
  
  return breadcrumbs
}

// Check if we're on a specific inspector page (not dashboard)
export const isInspectorSubpage = (pathname: string): boolean => {
  const fpCodeMatch = pathname.match(/\/inspect\/([^/]+)/)
  if (!fpCodeMatch) return false
  
  const fpCode = fpCodeMatch[1]
  const dashboardPath = `/inspect/${fpCode}`
  
  // Check if we're exactly on dashboard or on a subpage
  return pathname !== dashboardPath && pathname !== `${dashboardPath}/`
}